import { noOp } from 'common/Constants';
import React from 'react';
import { ButtonColors, ButtonSizes } from 'src/shared/design-system/Buttons';
import { FileUpload } from 'src/shared/design-system/FileUpload/FileUpload';
import { match } from 'ts-pattern';
import { usePhotoUpload } from './hooks';
import { Container, Photo, PhotoContainer, UploadButton } from './styles';
import { IUploadProps } from './types';

/**
 * Photo selector with drag and drop functionality for uploading.
 * Component does not automatically upload photo. Use `onChange`
 * callback to get the processed image to upload to server.
 */
export const PhotoUpload: React.FC<IUploadProps> = (props) => {
  const {
    autoUpload,
    accept = 'image/jpeg, image/png',
    buttonColor = ButtonColors.BLUE,
    className,
    changeButtonText = 'Change Image',
    disabled,
    error,
    id,
    invalidFileMessage,
    onChange = noOp,
    placeholder,
    placeholderContent,
    photoSize,
    readOnly = false,
    roundedPhoto,
    showPreview = true,
    showUploadButton = true,
    uploadButtonText = 'Upload Image',
    uploadingButtonText = 'Uploading...',
    value,
  } = props;

  const { handleChange, isUploading, imageSrc, maxSize } = usePhotoUpload({
    autoUpload,
    id,
    invalidFileMessage,
    onChange,
    readOnly,
    value,
  });

  return (
    <FileUpload
      accept={accept}
      className={className}
      disabled={disabled ?? readOnly}
      dropzoneComponent={Container}
      maxFiles={1}
      maxSize={maxSize}
      onChange={handleChange}>
      {(!imageSrc && placeholderContent) || !showPreview ? (
        placeholderContent
      ) : (
        <PhotoContainer disabled={disabled} error={error} photoSize={photoSize} readOnly={readOnly}>
          {!imageSrc && placeholder}
          {imageSrc && <Photo disabled={disabled} src={imageSrc} photoSize={photoSize} rounded={roundedPhoto} />}
        </PhotoContainer>
      )}
      {showUploadButton && (
        <UploadButton
          fluid
          disabled={disabled}
          error={error}
          readOnly={readOnly}
          color={buttonColor}
          photoSize={photoSize}
          size={ButtonSizes.M}>
          {match({ isUploading, hasImage: Boolean(imageSrc) })
            .with({ isUploading: true }, () => uploadingButtonText)
            .with({ hasImage: true }, () => changeButtonText)
            .otherwise(() => uploadButtonText)}
        </UploadButton>
      )}
    </FileUpload>
  );
};

export { Photo, PhotoContainer };
